import { useEffect, useRef, useState } from "react";
import type { DoctorQuestionSummary } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchDoctorQuestions,
  UnauthorizedError,
  withdrawDoctorQuestion,
} from "@/lib/clinician-api";
import {
  canWithdrawQuestion,
  doctorQuestionStatusText,
  handledHowText,
  stockholmDateTime,
} from "@/lib/medical-question";
import { useReadOnly } from "./SupportView";

// TES-1149 — the doctor's questions to this patient, under "Besked till
// patienten": each with the notice it was sent with, its status and her
// answer. The work queue's "Svar på läkarens fråga" is handled in its own
// row; this list is the patient-page view of the same questions
// (#lakarens-fragor).
//
// TES-1332 — the decision's statuses ("Inväntar patientsvar", "Besvarad",
// "Hanterad") and the times the decision names: when the notice was
// published and when she answered.
//
// Renders nothing until there is a question, so a patient who was never
// asked one gets no empty heading. `reloadKey` changes when a new notice is
// sent from the form beside it.
//
// TES-1335 — "läkaren uttryckligen återkallar frågan": a question she has
// not answered can be withdrawn here, after a confirmation. It then reads
// "Återkallad" for her too, the task leaves her checklist and Home, the
// history is kept, and she cannot answer it (Drive Ö-577, 2026-10-15).
//
// The staff-facing strings are proposed copy, except the statuses.
export function DoctorQuestions({
  patientId,
  onUnauthorized,
  reloadKey = 0,
}: {
  patientId: string;
  onUnauthorized: () => void;
  reloadKey?: number;
}) {
  const [questions, setQuestions] = useState<DoctorQuestionSummary[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // Callers pass an inline arrow; keeping it out of the effect's deps stops
  // every render from refetching.
  const unauthorized = useRef(onUnauthorized);
  unauthorized.current = onUnauthorized;
  const [withdrawn, setWithdrawn] = useState(0);

  useEffect(() => {
    let live = true;
    fetchDoctorQuestions(patientId)
      .then((res) => {
        if (!live) return;
        setQuestions(res.questions);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) {
          unauthorized.current();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [patientId, reloadKey, withdrawn]);

  if (error) {
    return (
      <p id="lakarens-fragor" className="text-xs text-danger" role="alert">
        Frågorna till patienten kunde inte hämtas: {error}
      </p>
    );
  }
  if (!questions || questions.length === 0) return null;

  return (
    <div id="lakarens-fragor" className="mt-md flex flex-col gap-sm">
      <h3 className="text-sm font-semibold">Frågor till patienten</h3>
      <ul className="flex flex-col gap-sm">
        {questions.map((q) => (
          <li
            key={q.taskId}
            className="rounded-md border border-surfaceMuted p-sm text-sm"
          >
            <DoctorQuestionDetail question={q} />
            <WithdrawQuestion
              patientId={patientId}
              question={q}
              onWithdrawn={() => setWithdrawn((n) => n + 1)}
              onUnauthorized={() => unauthorized.current()}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * TES-1335 — "Återkalla frågan", with a confirmation step: withdrawing is
 * final (a new question is a new notice).
 */
export function WithdrawQuestion({
  patientId,
  question: q,
  onWithdrawn,
  onUnauthorized,
}: {
  patientId: string;
  question: DoctorQuestionSummary;
  onWithdrawn: () => void;
  onUnauthorized: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!canWithdrawQuestion({ q, role: claims?.role, readOnly })) return null;

  async function withdraw() {
    setBusy(true);
    setError(null);
    try {
      await withdrawDoctorQuestion(patientId, q.taskId);
      setConfirming(false);
      onWithdrawn();
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      else if (
        e instanceof Error &&
        e.message === "doctor_question_answered_not_withdrawable"
      ) {
        setError(
          "Patienten har redan svarat. En besvarad fråga kan inte återkallas.",
        );
        onWithdrawn();
      } else setError("Frågan kunde inte återkallas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  const btn =
    "rounded-md border border-surfaceMuted px-sm py-xs text-xs text-textSecondary hover:text-textPrimary disabled:opacity-50";
  return (
    <div className="mt-sm flex flex-col gap-xs">
      {confirming ? (
        <div className="flex flex-wrap items-center gap-sm">
          <span className="text-xs text-textSecondary">
            Patienten kan då inte längre svara. Frågan visas som återkallad,
            uppgiften försvinner och historiken bevaras.
          </span>
          <button
            type="button"
            className={btn}
            disabled={busy}
            onClick={() => void withdraw()}
          >
            {busy ? "Återkallar…" : "Ja, återkalla frågan"}
          </button>
          <button
            type="button"
            className={btn}
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            Avbryt
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={`${btn} self-start`}
          onClick={() => setConfirming(true)}
        >
          Återkalla frågan
        </button>
      )}
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One question with its notice, status, times and her full answer — shared
 * by the patient page and the queue's expanded "Svar på läkarens fråga" row.
 */
export function DoctorQuestionDetail({
  question: q,
  noticeOpen = false,
}: {
  question: DoctorQuestionSummary;
  /** Show the notice expanded (the queue row) rather than folded. */
  noticeOpen?: boolean;
}) {
  return (
    <>
      <div className="flex items-start justify-between gap-sm">
        <p className="font-medium whitespace-pre-wrap">{q.question}</p>
        <span
          className={
            q.status === "waiting" || q.status === "withdrawn"
              ? "shrink-0 rounded-sm bg-surfaceMuted px-xs text-xs text-textSecondary"
              : "shrink-0 rounded-sm bg-brand/10 px-xs text-xs text-brand"
          }
        >
          {doctorQuestionStatusText(q)}
        </span>
      </div>
      <p className="mt-xs text-xs text-textSecondary">
        Publicerad {stockholmDateTime(q.noticePublishedAt ?? q.askedAt)}
        {q.askedBy ? ` av ${q.askedBy}` : ""}
        {q.answeredAt ? ` · Besvarad ${stockholmDateTime(q.answeredAt)}` : ""}
        {q.handledAt
          ? ` · Hanterad ${stockholmDateTime(q.handledAt)}${
              q.handledBy ? ` av ${q.handledBy}` : ""
            }${handledHowText(q)}`
          : ""}
        {q.withdrawnAt
          ? ` · Återkallad ${stockholmDateTime(q.withdrawnAt)}${
              q.withdrawnBy ? ` av ${q.withdrawnBy}` : ""
            }`
          : ""}
        {q.uploads > 0 ? ` · ${q.uploads} uppladdad fil(er)` : ""}
      </p>
      {q.noticeBody ? (
        <details className="mt-xs text-xs" open={noticeOpen}>
          <summary className="cursor-pointer text-textSecondary">
            Beskedet frågan skickades med
          </summary>
          <p className="mt-xs whitespace-pre-wrap">{q.noticeBody}</p>
        </details>
      ) : null}
      {q.answer !== null ? (
        <div className="mt-sm rounded-md bg-brand/5 p-xs">
          <p className="text-xs text-textSecondary">Patientens svar</p>
          <p className="whitespace-pre-wrap">{q.answer}</p>
        </div>
      ) : null}
    </>
  );
}
